import type { SourceType } from '~/features-swym/aura/interfaces/ai'
import type { AIAssistantName } from '~/features-swym/aura/interfaces/ai-assistants'
import type {
  AIStreamDataChunkRephrasingQueries,
  AIStreamDataChunkSources,
  AIStreamDataSourceDetails,
  ExternalSourceDetails,
  PlatformSourceDetails,
  StreamDataSource,
} from '~/features-swym/aura/interfaces/stream-data'
import { isExternalSource, isPlatformSource } from '~/features-swym/aura/utils/stream-data'

/**
 * Parse the AI sources stream data.
 * @param data Data received.
 * @returns Data parsed.
 */
export function parseStreamDataSources(data: AIStreamDataChunkSources): Partial<AIStreamDataSourceDetails>[] {
  const sources = new Map<string, Partial<AIStreamDataSourceDetails>>()

  const dataHits = data.hits.hits
  if (dataHits) {
    // order the sources by their score
    dataHits.sort((a: StreamDataSource, b: StreamDataSource): number => {
      if (a._source.reranker__score && b._source.reranker__score)
        return b._source.reranker__score - a._source.reranker__score
      return 0
    })
    dataHits.forEach((osData: StreamDataSource): void => {
      const sourceDetails = osData?._source
      // if the source is of type platform
      if (isPlatformSource(sourceDetails)) {
        const platformSourceDetails = sourceDetails as PlatformSourceDetails
        const contentUri = platformSourceDetails.x3dcontent__content__id
          ?? platformSourceDetails.x3dcontent__content__i3dx as string
        const sourceData = {
          type: 'platform' as SourceType,
          swym__content__uri: platformSourceDetails.swym__content__uri,
          swym__community__uri: platformSourceDetails.swym__community__uri,
          tenant_id: platformSourceDetails.tenant_id,
          x3dcontent__content__id: platformSourceDetails.x3dcontent__content__id,
          x3dcontent__protocol__version: platformSourceDetails.x3dcontent__protocol__version,
          x3dcontent__app__id: platformSourceDetails.x3dcontent__app__id,
          x3dcontent__service__id: platformSourceDetails.x3dcontent__service__id,
          x3dcontent__content__i3dx: platformSourceDetails.x3dcontent__content__i3dx,
          x3dcontent__content__type: platformSourceDetails.x3dcontent__content__type,
        }

        sources.set(contentUri, sourceData)
      }
      // if the source is of type external
      else if (isExternalSource(sourceDetails)) {
        const externalSourceDetails = sourceDetails as ExternalSourceDetails
        const contentUri = externalSourceDetails.content__url
        const sourceData = {
          type: 'external' as SourceType,
          content__url: externalSourceDetails.content__url,
          content__title: externalSourceDetails.content__title,
        }

        sources.set(contentUri, sourceData)
      }
    })
  }
  return [...sources.values()]
}

/**
 * Parse the AI Assistants API response.
 * @param assistantInfo Info about the AI Assistant.
 * @returns A list of names of available assistants.
 */
export function parseAIAssistants(assistantInfo: { assistant__name: string }[]): AIAssistantName[] {
  return assistantInfo.map(obj => obj.assistant__name) as AIAssistantName[]
}

/**
 * Parse the AI rephrasing stream data.
 * @param data Data received.
 * @returns an array of rephrasing queries
 */
export function parseStreamDataRephrasingQueries(data: AIStreamDataChunkRephrasingQueries): Partial<string>[] {
  return data.type === 'rephrasing' ? data.queries : []
}
